import { Injectable } from '@angular/core'
import { Observable, BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog'
import { IAutomaticPlanTypeSelectorData } from '../automatic-points/automatic-plan-type-selector/automatic-plan-type-selector.component'
import { IPACData } from '@app/collections/inspection-plan-assistant/inspection-plan-assistant.component'
import { ReportWindowViewModel } from 'projects/phdsoft/common/src/lib/models/report-window-data.model'

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  public loading = false

  public showReferenceDate = false
  public showElementTypeSelector = false

  public criticalAreaInsertion = false
  public boxMeasurementTool = false
  public elementInsertion = false
  public droneFlightPlan = false
  public droneInspectionRoute = false

  public showComponentList;
  public showImageOverlay = false
  public isSideBarExpanded;
  public isVisualizationModeOpen;
  public isVisualizationModeMinimized = false;
  public isAssetModeOpen;
  public isNavigationToolsOpen;
  public showAssetInfo = true;
  public showNavigationArrows = true;
  public showBaseBoard = false;
  public showGraphics = false;
  public showTimeline = false;
  public showInspectionList = false;
  public infoPositionOnBaseboardOpen = false;

  // Used to open dialogs with QrCodes
  public showManualInspectionInsertion = false;
  public showElementsFilter: boolean = false;
  public showSensorsWindow = false;
  public showChangesWorkflowComponent: boolean = false;
  public showFeaAction: string = "";
  public showFeaResults: boolean = false;
  public showConnections: boolean = false;
  public showPropertiesWindowOpen: boolean = false;
  public showCriticalAreaWindow: boolean = false
  public showElementPropertyWindow: boolean = false;
  public showGeometryExtrusionComponent: boolean = false
  public showMergePlateComponent: boolean = false
  public showCutElementComponent: boolean = false
  public showRepairShapeSelectorComponent: boolean = false
  public showCriticalAreaSelectorComponent: boolean = false
  public showElementInsertionTypeSelector: boolean = false
  /** "Change inspection target" tool form, shown as a sideboard (QA: new windows go on the sideboard). */
  public showChangeInspectionTargetSideboard: boolean = false
  public showGeometryModificationComponent: boolean = false
  public showLoadCycleCorrosionComponent: boolean = false
  public showRiskAssessment: boolean = false;
  public showConsequenceMap = false;
  public showQrCodeViewer = false;
  public showCriticalAreaBoxConfig: boolean = false;
  public showRiskShield: boolean = false;
  public showCollectionInspectionGauginsPositioningWizard: boolean = false;
  public showTypicalDetailsPositioning: boolean = false;
  public showAutomaticPlanAreaWizard: boolean = false;
  public showAutomaticPlanTypeSelector: boolean = false;
  public showDocumentsForm: boolean = false;
  public showDocumentsSearch: boolean = false;
  public showElementInsertionSelection: boolean = false;
  public elementInsertionType: string = '';
  public showReportWindow: boolean = false;
  public showSawInSar: boolean = false;
  public showAiAssistant: boolean = window.navigator.onLine;

  private dialogIdsToCheck: string[] = ["W1", "W2", "W5"];
  private openDialogIds = new BehaviorSubject<string[]>([]);
  private openComponents = new BehaviorSubject<string[]>([]);
  public showCopilotSideboard: boolean = true;
  public showChangesWorkflowSideboard: boolean = false;
  public showFeaTrackedElementsSelector: boolean = false;
  public showSyncOfflineSideboard: boolean = false;
  public changesWorkflowData: { changeId?: string } = {};

  openDialogs: string[] = [];

  private destroy$ = new Subject<void>();

  public automaticPlanTypeSelectorData: IAutomaticPlanTypeSelectorData;

  // Add these properties for the inspection plan assistant
  private _inspectionPlanAssistantData: IPACData;
  private _showInspectionPlanAssistant = false;

  // Report window data
  public reportWindowData: ReportWindowViewModel;

  constructor(
    public readonly snack: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.trackOpenDialogs();
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleComponentList(isOpen: boolean) {
    this.showComponentList = isOpen
  }

  toggleVisualizationMode(isOpen: boolean, minimized: boolean = false) {
    this.isVisualizationModeOpen = isOpen;
    this.isVisualizationModeMinimized = minimized;
  }

  toggleAssetMode(isOpen: boolean) {
    this.isAssetModeOpen = isOpen
  }


  toggleNavigationTools(isOpen: boolean) {
    this.isNavigationToolsOpen = isOpen
  }

  toggleShowAssetInfo(show: boolean) {
    this.showAssetInfo = show
  }

  toggleNavigationArrow(show: boolean) {
    this.showNavigationArrows = show
  }

  toggleManualInspectionInsertion(isOpen: boolean) {
    this.showManualInspectionInsertion = isOpen
  }

  toggleElementsFilter(value) {
    this.showElementsFilter = value;
  }

  toggleSensorsWindow(show: boolean): void {
    this.showSensorsWindow = show;
  }


  toggleFeaActionService(isOpen: boolean, feaMode: string) {
    // console.log("Activating FEA", isOpen, " feaMode: ",feaMode)
    this.showFeaResults = isOpen
    this.showFeaAction = feaMode
  }

  togglePropertiesWindow(isOpen: boolean) {
    this.showPropertiesWindowOpen = isOpen
  }

  toggleCriticalAreaWindow(isOpen: boolean) {
    this.showCriticalAreaWindow = isOpen
  }

  toggleElementPropertyWindow(isOpen: boolean) {
    this.showElementPropertyWindow = isOpen
  }

  toggleConsequenceMap(value) {
    //console.log('Toggle consequence map:', show); // Debug log
    this.showConsequenceMap = value;
  }

  toggleQrCodeViewer(value: boolean): void {
    this.showQrCodeViewer = value;
  }

  toggleRiskShield(value: boolean): void {
    this.showRiskShield = value;
  }

  toggleCollectionInspectionGauginsPositioningWizard(value: boolean): void {
    this.showCollectionInspectionGauginsPositioningWizard = value;
  }

  toggleTypicalDetailsPositioning(value: boolean): void {
    this.showTypicalDetailsPositioning = value;
  }

  toggleAutomaticPlanAreaWizard(value: boolean): void {
    this.showAutomaticPlanAreaWizard = value;
  }

  toggleAutomaticPlanTypeSelector(value: boolean): void {
    this.showAutomaticPlanTypeSelector = value;
  }

  toggleAiAssistant(value: boolean): void {
    this.showAiAssistant = value;
  }

  toggleInspectionPlanAssistant(show: boolean, data?: IPACData): void {
    // Store the data if provided
    if (data) {
      this._inspectionPlanAssistantData = data;
    }

    // Set visibility
    this._showInspectionPlanAssistant = show;

    // When closing the assistant and we have data of type 'Automatic'
    if (!show && this._inspectionPlanAssistantData?.type === 'Automatic') {
      const automaticPlanData = this.automaticPlanTypeSelectorData;

      if (automaticPlanData) {
        // Open the appropriate wizard based on the inspection method
        if (automaticPlanData.inspectionMethodName === 'Point') {
          setTimeout(() => {
            this.toggleCollectionInspectionGauginsPositioningWizard(true);
          }, 0);
        } else if (automaticPlanData.inspectionMethodName === 'Area') {
          setTimeout(() => {
            this.toggleAutomaticPlanAreaWizard(true);
          }, 0);
        }
      }
    }
  }

  toggleDocumentsForm(show: boolean): void {
    this.showDocumentsForm = show;
  }

  toggleDocumentsSearch(show: boolean): void {
    this.showDocumentsSearch = show;
  }

  toggleChangesWorkflowSideboard(show: boolean, data?: { changeId?: string }): void {
    this.showChangesWorkflowSideboard = show;
    this.changesWorkflowData = data;
  }

  toggleFeaTrackedElementsSelector(show: boolean): void {
    this.showFeaTrackedElementsSelector = show;
  }

  toggleSyncOfflineSideboard(show: boolean): void {
    this.showSyncOfflineSideboard = show;
  }

  toggleElementInsertionSelection(show: boolean, type?: string): void {
    if (type) {
      this.elementInsertionType = type;
    }
    this.showElementInsertionSelection = show;
  }

  toggleSawInSar(value: boolean) {
    this.showSawInSar = value;
  }

  toggleReportWindow(value: boolean) {
    this.showReportWindow = value;

    // When closing the window, reset the reportWindowData to ensure it doesn't maintain old state
    if (!value) {
      this.reportWindowData = null;
    }
  }

  getOpenComponents(): Observable<string[]> {
    const componentFlags = [
      { flag: this.showElementsFilter, componentId: "W3" },
      { flag: this.showComponentList, componentId: "W5" },
      { flag: this.showBaseBoard, componentId: "BB" },
      { flag: this.showInspectionList, componentId: "IL" },
      { flag: this.showManualInspectionInsertion, componentId: "MII" },
      { flag: this.infoPositionOnBaseboardOpen, componentId: "W6" },
      { flag: this.showGraphics, componentId: "W9" },
      { flag: this.showFeaResults, componentId: this.showFeaAction },
      { flag: this.showCriticalAreaWindow, componentId: "CA4" },
      { flag: this.showPropertiesWindowOpen, componentId: "PW2" },
      { flag: this.showElementPropertyWindow, componentId: "PW4" },
    ];

    const openComponentIds = componentFlags
      .filter(item => item.flag)
      .map(item => item.componentId);

    // Emit the open component IDs
    this.openComponents.next(openComponentIds.length > 0 ? openComponentIds : []);

    return this.openComponents.asObservable();
  }

  private updateOpenDialogIds(): void {
    const openIds = this.dialog.openDialogs
      .filter(dialogRef =>
        dialogRef.componentInstance.data && this.dialogIdsToCheck.includes(dialogRef.componentInstance.data.id)
      )
      .map(dialogRef => dialogRef.componentInstance.data.id);

    if (openIds.length > 0) {
      // If there are open dialogs, emit their IDs
      this.openDialogIds.next(openIds);
    } else {
      // No open dialogs, let's check for open components
      // console.log("No dialogs open, checking for open components...");
      this.getOpenComponents()
      .pipe(takeUntil(this.destroy$))
      .subscribe(openComponentIds => {
        this.openDialogIds.next(openComponentIds)
        // console.log("Open components:", openComponentIds);
      });
    }
  }

  private trackOpenDialogs(): void {
    // Update the BehaviorSubject whenever dialog state changes
    this.dialog.afterOpened
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateOpenDialogIds());

    this.dialog.afterAllClosed
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => this.updateOpenDialogIds());
  }

  getOpenDialogIds(): Observable<string[]> {
    return this.openDialogIds.asObservable();
  }

  openTimeline() {
    this.showTimeline = !this.showTimeline;
    if (!this.showGraphics && !this.showTimeline) {
      this.infoPositionOnBaseboardOpen = false;
    } else {
      this.infoPositionOnBaseboardOpen = true;
    }
  }

  toggleLoadCycleCorrosion(value: boolean): void {
    this.showLoadCycleCorrosionComponent = value;
  }

  // Add these getter/setter methods
  public get showInspectionPlanAssistant(): boolean {
    return this._showInspectionPlanAssistant;
  }

  public get inspectionPlanAssistantData(): IPACData {
    return this._inspectionPlanAssistantData;
  }
}
